import React, { useState, useEffect, useCallback } from 'react';
import { Container, CssBaseline, Box, CircularProgress } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import {
  Navigation,
  CalendarView,
  ExerciseSummaryView,
  ExerciseListView,
  BodyWeightProgressionView,
  UserProfile,
  ProfileSetup,
  ServiceUnavailableView,
  RoutinesView,
  ExportButton,
  ReleaseNotes,
  FriendsView,
  WelcomePage,
  ChatPage,
  Results2025,
  TrainingProgramsView,
} from './components';
import { EvaluationView } from './components/evaluation/EvaluationView';
import { useAuth } from './hooks/useAuth';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './i18n/LanguageContext';
import { TimerProvider } from './contexts/TimerContext';
import { ExerciseProvider } from './contexts/ExerciseContext';
import { WorkoutProvider } from './contexts/WorkoutContext';
import { BodyWeightProvider } from './contexts/BodyWeightContext';
import { useExercise } from './contexts/ExerciseContext';
import { API_URL } from './utils';
import { theme } from './theme';
import { BrowserRouter as Router } from 'react-router-dom';
import releaseNotes from './data/releaseNotes.json';

interface AppProps {
  isServiceUnavailable: boolean;
  setIsServiceUnavailable: (value: boolean) => void;
}

function App({ isServiceUnavailable, setIsServiceUnavailable }: AppProps) {
  const {
    idToken,
    fetchWithAuth,
    scriptError,
    loginRefreshKey,
    setLoginRefreshKey,
    isExchangingToken
  } = useAuth();

  const { reloadExercises } = useExercise();

  const [view, setView] = useState<'calendar' | 'summary' | 'exercises' | 'bodyweight' | 'routines' | 'programs' | 'friends' | 'profile' | 'chat' | 'evaluation' | 'wrapped'>('calendar');
  const [initialFriendsTab, setInitialFriendsTab] = useState<number | undefined>(undefined);

  const handleSetView = (
    targetView: 'calendar' | 'summary' | 'exercises' | 'bodyweight' | 'routines' | 'programs' | 'friends' | 'profile' | 'chat' | 'evaluation' | 'wrapped',
    tab?: number
  ) => {
    setView(targetView);
    if (targetView === 'friends') {
      setInitialFriendsTab(tab);
    }
  };

  const [releaseNotesOpen, setReleaseNotesOpen] = useState(false);
  const [needsProfileSetup, setNeedsProfileSetup] = useState<boolean | null>(null);
  const [profileSetupChecked, setProfileSetupChecked] = useState(false);

  // Add notification refresh function state
  const [notificationRefresh, setNotificationRefresh] = useState<(() => Promise<void>) | null>(null);


  // Check profile setup status on login
  useEffect(() => {
    if (idToken && !profileSetupChecked) {
      const checkProfileSetup = async () => {
        try {
          const response = await fetchWithAuth(`${API_URL}/user/profile-setup-status`);
          if (response.ok) {
            const data = await response.json();
            setNeedsProfileSetup(data.needsSetup);
          }
        } catch (error) {
          console.error('Error checking profile setup status:', error);
          setNeedsProfileSetup(false); // Default to not needing setup on error
        } finally {
          setProfileSetupChecked(true);
        }
      };

      checkProfileSetup();
    }
  }, [idToken, profileSetupChecked, fetchWithAuth]);

  // Show release notes on first login or version update
  useEffect(() => {
    if (idToken && !needsProfileSetup && profileSetupChecked) {
      const currentVersion = releaseNotes.currentVersion;
      const lastSeenVersion = localStorage.getItem('lastSeenReleaseNotesVersion');

      if (lastSeenVersion !== currentVersion) {
        // Show release notes after a short delay to let the app load
        const timer = setTimeout(() => {
          setReleaseNotesOpen(true);
        }, 1000);

        return () => clearTimeout(timer);
      }
    }
  }, [idToken, needsProfileSetup, profileSetupChecked]);

  const handleCloseReleaseNotes = () => {
    setReleaseNotesOpen(false);
    const currentVersion = releaseNotes.currentVersion;
    localStorage.setItem('lastSeenReleaseNotesVersion', currentVersion);
  };

  // Health check to detect backend availability early
  useEffect(() => {
    if (!idToken) return;

    const checkBackendHealth = async () => {
      try {
        // Use a simple health check endpoint or the exercises endpoint
        const response = await fetch(`${API_URL}/health`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        });

        if (response.ok) {
          console.log('Backend health check passed');
          setIsServiceUnavailable(false);
        } else {
          console.log('Backend health check failed with status:', response.status);
          // Don't set service unavailable for auth errors (401, 403)
          if (response.status !== 401 && response.status !== 403) {
            setIsServiceUnavailable(true);
          }
        }
      } catch (error: any) {
        console.error('Backend health check failed:', error);
        // Check if this looks like a network/connection error
        if (
          error.name === 'TypeError' ||
          error.message.includes('fetch') ||
          error.message.includes('network') ||
          error.message.includes('connection')
        ) {
          setIsServiceUnavailable(true);
        }
      }
    };

    // Run health check immediately and then periodically if service is unavailable
    checkBackendHealth();
  }, [idToken, loginRefreshKey, setIsServiceUnavailable]);


  const handleRetryLoad = () => {
    setIsServiceUnavailable(false);
    // Trigger login refresh to re-run health check and data loading
    setLoginRefreshKey((prev: number) => prev + 1);
  };

  const handleServiceUnavailable = () => {
    setIsServiceUnavailable(true);
  };

  const handleReleaseNotesClick = () => {
    setReleaseNotesOpen(true);
  };

  const handleProfileSetupComplete = () => {
    setNeedsProfileSetup(false);
  };

  if (isServiceUnavailable) {
    return <ServiceUnavailableView onRetry={handleRetryLoad} />;
  }

  if (!idToken) {
    return (
      <WelcomePage
        scriptError={scriptError}
        isExchangingToken={isExchangingToken}
      />
    );
  }

  // Show profile setup if needed
  if (needsProfileSetup === true) {
    return (
      <ProfileSetup
        onComplete={handleProfileSetupComplete}
      />
    );
  }

  // Show loading while checking profile setup status
  if (needsProfileSetup === null && !profileSetupChecked) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Navigation
        view={view}
        setView={handleSetView}
        onReleaseNotesClick={handleReleaseNotesClick}
        fetchWithAuth={fetchWithAuth}
        onNotificationRefresh={setNotificationRefresh}
        authToken={idToken}
      />
      <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
        {view === 'calendar' ? (
          <CalendarView setView={handleSetView} onServiceUnavailable={handleServiceUnavailable} />
        ) : view === 'summary' ? (
          <ExerciseSummaryView fetchWithAuth={fetchWithAuth} onServiceUnavailable={handleServiceUnavailable} />
        ) : view === 'exercises' ? (
          <ExerciseListView fetchWithAuth={fetchWithAuth} onServiceUnavailable={handleServiceUnavailable} reloadExercises={reloadExercises} />
        ) : view === 'bodyweight' ? (
          <BodyWeightProgressionView fetchWithAuth={fetchWithAuth} onServiceUnavailable={handleServiceUnavailable} />
        ) : view === 'routines' ? (
          <RoutinesView fetchWithAuth={fetchWithAuth} onServiceUnavailable={handleServiceUnavailable} notificationRefresh={notificationRefresh} />
        ) : view === 'programs' ? (
          <TrainingProgramsView />
        ) : view === 'friends' ? (
          <FriendsView fetchWithAuth={fetchWithAuth} notificationRefresh={notificationRefresh} initialTab={initialFriendsTab} />
        ) : view === 'profile' ? (
          <>
            <ExportButton fetchWithAuth={fetchWithAuth} />
            <UserProfile onServiceUnavailable={handleServiceUnavailable} />
          </>
        ) : view === 'chat' ? (
          <ChatPage />
        ) : view === 'evaluation' && process.env.NODE_ENV === 'development' ? (
          <EvaluationView fetchWithAuth={fetchWithAuth} />
        ) : view === 'wrapped' ? (
          <Results2025 />
        ) : null}
      </Container>
      <ReleaseNotes
        open={releaseNotesOpen}
        onClose={handleCloseReleaseNotes}
      />
    </>
  );
}

const AppWrapper: React.FC = () => {
  const [isServiceUnavailable, setIsServiceUnavailable] = useState(false);

  const handleServiceUnavailable = useCallback(() => {
    setIsServiceUnavailable(true);
  }, []);

  return (
    <ExerciseProvider onServiceUnavailable={handleServiceUnavailable}>
      <WorkoutProvider onServiceUnavailable={handleServiceUnavailable}>
        <BodyWeightProvider onServiceUnavailable={handleServiceUnavailable}>
          <App
            isServiceUnavailable={isServiceUnavailable}
            setIsServiceUnavailable={setIsServiceUnavailable}
          />
        </BodyWeightProvider>
      </WorkoutProvider>
    </ExerciseProvider>
  );
};

const Root: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <LanguageProvider>
            <TimerProvider>
              <AppWrapper />
            </TimerProvider>
          </LanguageProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
};

export default Root; 